
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText } from "lucide-react";

interface NewTermsStepProps {
  onComplete: () => void;
}

export const NewTermsStep = ({ onComplete }: NewTermsStepProps) => {
  const [hasAccepted, setHasAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleAccept = async () => {
    if (!user || !hasAccepted) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('user_terms_acceptance' as any)
        .insert({
          user_id: user.id,
          terms_version: '1.0',
          ip_address: null
        });

      if (error) throw error;

      toast({
        title: "Termos aceitos",
        description: "Termos de uso aceitos com sucesso!"
      });

      onComplete();
    } catch (error: any) {
      console.error('Erro ao aceitar termos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a aceitação dos termos. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <FileText className="w-12 h-12 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl font-semibold text-card-foreground mb-2">Termos de Uso</h2>
        <p className="text-muted-foreground">
          Leia e aceite nossos termos de uso para continuar
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Termos e Condições - Conversion Bonus</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64 w-full rounded-md border p-4">
            <div className="space-y-4 text-sm text-card-foreground leading-relaxed">
              <section>
                <h4 className="font-medium mb-2">1. Aceitação dos Termos</h4>
                <p>
                  Ao utilizar a plataforma Conversion Bonus, você concorda em cumprir e ficar vinculado a estes 
                  Termos de Uso. Se você não concorda com qualquer parte destes termos, não deve usar nossos serviços.
                </p>
              </section>

              <section>
                <h4 className="font-medium mb-2">2. Descrição do Serviço</h4>
                <p>
                  O Conversion Bonus é uma plataforma de marketing digital que oferece ferramentas para 
                  gerenciamento de programas de cashback e indicações para empresas, facilitando o 
                  engajamento com clientes e o crescimento de vendas.
                </p>
              </section>

              <section>
                <h4 className="font-medium mb-2">3. Elegibilidade</h4>
                <p>
                  Para usar nossos serviços, você deve ter pelo menos 18 anos de idade e ter capacidade 
                  legal para celebrar contratos. Ao se registrar, você garante que todas as informações 
                  fornecidas são verdadeiras, precisas e completas.
                </p>
              </section>

              <section>
                <h4 className="font-medium mb-2">4. Privacidade e Proteção de Dados</h4>
                <p>
                  Respeitamos sua privacidade e nos comprometemos a proteger seus dados pessoais de 
                  acordo com a Lei Geral de Proteção de Dados (LGPD). Nossa Política de Privacidade 
                  descreve como coletamos, usamos e protegemos suas informações.
                </p>
              </section>

              <section>
                <h4 className="font-medium mb-2">5. Uso Aceitável</h4>
                <p>
                  Você concorda em usar nossos serviços apenas para fins legais e de acordo com estes 
                  Termos. É proibido usar a plataforma para atividades fraudulentas, spam, ou qualquer 
                  outra atividade que possa prejudicar nossos serviços ou outros usuários.
                </p>
              </section>

              <section>
                <h4 className="font-medium mb-2">6. Limitação de Responsabilidade</h4>
                <p>
                  Em nenhuma circunstância seremos responsáveis por danos indiretos, incidentais, 
                  especiais, consequenciais ou punitivos, incluindo perda de lucros, dados ou uso.
                </p>
              </section>

              <div className="mt-6 p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground text-center">
                  <strong>Última atualização:</strong> Janeiro de 2025 | <strong>Versão:</strong> 1.0
                </p>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Checkbox 
            id="accept-terms" 
            checked={hasAccepted} 
            onCheckedChange={(checked) => setHasAccepted(checked as boolean)} 
            disabled={isLoading} 
          />
          <label htmlFor="accept-terms" className="text-sm leading-relaxed cursor-pointer text-card-foreground">
            Li e concordo com os termos de uso do Conversion Bonus. Compreendo que ao aceitar, 
            estou juridicamente vinculado a estes termos e condições.
          </label>
        </div>

        <Button 
          onClick={handleAccept} 
          disabled={!hasAccepted || isLoading} 
          size="lg" 
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processando...
            </>
          ) : (
            "Aceitar e Continuar"
          )}
        </Button>
      </div>
    </div>
  );
};
